import { createRoute } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { MessageSquarePlus, Sparkles } from "@fleet/icons";
// FLAGS: the entire page is gated by feature (active org).
import { withFeatureGate } from "@/features/flags/feature-gate";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer, PageHeader } from "@fleet/ui";
import { TutorChat } from "@/features/ai-tutor/tutor-chat";
import { useConversations } from "@/features/ai-tutor/api";

function AiTutorPage() {
  const { t } = useI18n();
  const conversations = useConversations();
  // `new-<n>` forces a blank chat; an id opens that conversation (keyed remount).
  const [chatKey, setChatKey] = createSignal("new-0");
  let counter = 0;

  const startNew = () => {
    counter += 1;
    setChatKey(`new-${counter}`);
  };

  return (
    <PageContainer maxWidth="6xl">
      <div class="flex items-start justify-between gap-2">
        <PageHeader
          title={t(msg`AI Tutor`)}
          description={t(
            msg`Ask about your course and get answers grounded in the material, 24/7.`,
          )}
          icon={Sparkles}
        />
      </div>

      <div class="mt-6 grid gap-6 md:grid-cols-[260px_1fr]">
        {/* Historial */}
        <aside class="space-y-2">
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-lg border border-border/40 px-3 py-2 text-sm font-medium hover:bg-primary/10"
            onClick={startNew}
            data-testid="ai-new-conversation"
          >
            <MessageSquarePlus class="h-4 w-4" />
            {t(msg`New conversation`)}
          </button>
          <div class="space-y-1" data-testid="ai-conversation-list">
            <For each={conversations.data ?? []}>
              {(conv) => (
                <button
                  type="button"
                  class="w-full truncate rounded-md px-3 py-2 text-left text-sm hover:bg-muted/60"
                  classList={{ "bg-muted/60": chatKey() === conv.id }}
                  onClick={() => setChatKey(conv.id)}
                  data-testid="ai-conversation-item"
                >
                  <span class="block truncate">
                    {conv.kind === "submission_feedback" ? "📝 " : ""}
                    {conv.title ?? t(msg`Conversation`)}
                  </span>
                </button>
              )}
            </For>
          </div>
        </aside>

        {/* Chat */}
        <section class="rounded-xl border border-border/40 p-4">
          <Show when={chatKey()} keyed>
            {(key) => <TutorChat conversationId={key.startsWith("new-") ? undefined : key} />}
          </Show>
        </section>
      </div>
    </PageContainer>
  );
}

export const aiTutorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ai-tutor",
  component: withFeatureGate("academy.ai_tutor", AiTutorPage),
});
